/* eslint-disable max-lines */
import { useForm, useFormState } from 'react-final-form'
import cx from 'classnames'
import { useFlags } from 'launchdarkly-react-client-sdk'

import Accordion from 'common/components/accordion/Accordion'
import Button from 'common/components/button'
import DynamicFields from 'common/components/dynamicFields'
import { FieldsConfiguration } from 'common/components/dynamicFields/interfaces'
import Textarea from 'common/components/textarea/Textarea'
import { useAppSelector } from 'common/hooks/redux'
import { ArrowRight as ArrowRightIcon, Spinner } from 'common/icons'
import { useContent } from 'features/unifiedFlow/contentPage/hooks'

import { BRIEF_ELEMENT_ID } from '../../components/universalBriefElement/constants'
import { getStepStatus } from '../../helpers'
import ApplyBriefToAllTemplatesCheckbox from '../components/ApplyBriefToAllTemplatesCheckbox'

import { BriefAnalysisResult } from './hooks/useDebouncedBriefAnalysis'
import BrandVoiceSettings from './brandVoiceSettings'
import { PageState } from './BriefPage'

type Props = {
  isDisabled: boolean
  isLoading: boolean
  fieldsConfiguration: FieldsConfiguration
  pageState: PageState
  brandVoiceId?: string
  isOptimized?: boolean
  briefField?: any
  briefText?: string
  setBriefText?: (text: string) => void
  activeTab?: 'describe' | 'paste'
  setActiveTab?: (tab: 'describe' | 'paste') => void
  selectedCustomerJourney?: any
  setShowCustomerJourneyModal?: (show: boolean) => void
  isBriefSectionCollapsed?: boolean
  setIsBriefSectionCollapsed?: (collapsed: boolean) => void
  briefQuality?: {
    criteria: Array<{ met: boolean; score: number; label: string }>
    percentage: number
    quality: string
  }
  isAnalyzingBrief?: boolean
  briefAnalysisError?: Error | null
  analysisResult?: BriefAnalysisResult | null
  hasSavedBrief?: boolean
  hasBriefChanged?: boolean
}

const DynamicForm = ({
  isDisabled,
  fieldsConfiguration,
  isLoading,
  pageState,
  brandVoiceId,
  isOptimized,
  briefField,
  briefText,
  setBriefText,
  activeTab,
  setActiveTab,
  selectedCustomerJourney,
  setShowCustomerJourneyModal,
  isBriefSectionCollapsed,
  setIsBriefSectionCollapsed,
  briefQuality,
  isAnalyzingBrief,
  briefAnalysisError,
  analysisResult,
  hasSavedBrief,
  hasBriefChanged,
}: Props) => {
  const form = useForm()
  const formState = useFormState()
  const { content } = useContent()
  const flags = useFlags()

  const selectedElementId = useAppSelector(
    (state) => state.unifiedFlow.selectedElementId
  )
  const isUniversalBriefSelected = selectedElementId === BRIEF_ELEMENT_ID

  const briefStepsStatus = getStepStatus(content?.steps_status, 'brief')

  const nonBriefFields = briefField
    ? fieldsConfiguration?.filter((field) => field.name !== briefField.name)
    : fieldsConfiguration

  const isNewFlow = !!briefField

  return (
    <>
      <div className="w-full">
        {!isNewFlow ? (
          <div className="w-150">
            <DynamicFields
              fieldsConfiguration={fieldsConfiguration}
              isDisabled={isDisabled}
            />
          </div>
        ) : (
          nonBriefFields &&
          nonBriefFields.length > 0 && (
            <div className="w-150 mb-6">
              <DynamicFields
                fieldsConfiguration={nonBriefFields}
                isDisabled={isDisabled}
              />
            </div>
          )
        )}

        {isNewFlow && briefField && (
          <>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <h2 className="text-2xl font-medium text-base-700">BRIEF</h2>
              </div>
              <div className="flex items-center gap-4">
                {!isNewFlow && (
                  <button
                    type="button"
                    className="text-sm text-base-700 hover:underline"
                  >
                    ADVANCED SETTINGS
                  </button>
                )}
                <button
                  type="button"
                  onClick={() =>
                    setIsBriefSectionCollapsed?.(!isBriefSectionCollapsed)
                  }
                  className="text-sm text-base-700 hover:underline flex items-center gap-1"
                >
                  {isBriefSectionCollapsed ? 'SHOW' : 'HIDE'}
                  <span>{isBriefSectionCollapsed ? '↓' : '↑'}</span>
                </button>
              </div>
            </div>

            {!isBriefSectionCollapsed && (
              <>
                <div className="mb-6">
                  <div className="text-base-700 font-medium mb-2">
                    Customer journey
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowCustomerJourneyModal?.(true)}
                    className="w-full p-3 border border-gold-400 rounded text-left text-base-700 bg-white hover:bg-gold-40 transition-colors flex items-center justify-between"
                  >
                    <span>
                      {selectedCustomerJourney?.customerJourneyTitle || ''}
                    </span>
                    <svg
                      className="w-4 h-4 text-base-700"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                </div>

                <div className="flex items-center justify-between mb-2 mt-10">
                  <div className="flex">
                    <button
                      type="button"
                      onClick={() => setActiveTab?.('describe')}
                      className={cx(
                        'py-2 px-4 text-xs text-base-700 rounded-2xl border border-gold-300 bg-yellow-400'
                      )}
                    >
                      DESCRIBE YOUR BRIEF
                    </button>
                  </div>
                </div>

                <div className="mb-2">
                  <Textarea
                    value={briefText || ''}
                    onChange={(e) => setBriefText?.(e.target.value)}
                    placeholder="Write this in natural language and only include the details you want included in your content. We'll use your input to generate a brief."
                    maxLength={500}
                    showCharacterCount
                    style={{ minHeight: '70px' }}
                  />
                </div>
                {isNewFlow && (
                  <p className="text-sm text-base-500 mb-6">
                    Write this in natural language and only include the details
                    you want included in your content. We’ll use your input to
                    generate a brief.
                  </p>
                )}

                {((briefQuality?.criteria &&
                  briefQuality.criteria.length > 0) ||
                  analysisResult ||
                  isAnalyzingBrief ||
                  !hasSavedBrief ||
                  hasBriefChanged) &&
                  !(briefText?.trim() === '' && !analysisResult) && (
                    <div className="mb-6 bg-gold-100 border border-gold-200 rounded-sm p-4">
                      <Accordion className="">
                        <Accordion.Item
                          isOpen={
                            !!analysisResult ||
                            isAnalyzingBrief ||
                            (briefQuality?.criteria.length ?? 0) > 0
                          }
                          showLeftCaret={true}
                          title={
                            <div className="flex items-center justify-between w-full">
                              <span className="text-base-700">
                                {(() => {
                                  if (isAnalyzingBrief) {
                                    return 'Brief quality'
                                  }

                                  if (
                                    (briefQuality?.criteria.length ?? 0) > 0
                                  ) {
                                    return `Brief quality : ${
                                      briefQuality?.quality || ''
                                    }`
                                  }

                                  return 'Brief quality'
                                })()}
                              </span>
                              {isAnalyzingBrief ? (
                                <Spinner
                                  className="w-4 h-4 text-base-700 animate-spin ml-auto"
                                  isDefaultColor={false}
                                />
                              ) : (
                                !briefAnalysisError &&
                                (briefQuality?.criteria.length ?? 0) > 0 && (
                                  <span className="text-base-700 ml-auto font-medium">
                                    {(() => {
                                      const percentage =
                                        briefQuality?.percentage ?? 0
                                      const discreteValues = [
                                        0, 25, 50, 75, 100,
                                      ]
                                      const displayPercentage =
                                        discreteValues.reduce((prev, curr) =>
                                          Math.abs(curr - percentage) <
                                          Math.abs(prev - percentage)
                                            ? curr
                                            : prev
                                        )
                                      return `${displayPercentage}%`
                                    })()}
                                  </span>
                                )
                              )}
                            </div>
                          }
                        >
                          {isAnalyzingBrief ? (
                            <div className="flex items-center gap-3">
                              <div className="flex-1">
                                <div className="h-2 bg-gold-200 rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-gold-400 rounded-full animate-pulse"
                                    style={{ width: '60%' }}
                                  />
                                </div>
                              </div>
                              <div className="text-sm text-base-500 animate-pulse">
                                Analyzing...
                              </div>
                            </div>
                          ) : briefAnalysisError ? (
                            <div className="text-sm text-red-600">
                              Error analyzing brief:{' '}
                              {briefAnalysisError.message}
                            </div>
                          ) : (
                            <>
                              <div className="mb-6">
                                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                  {(() => {
                                    const percentage =
                                      briefQuality?.percentage ?? 0
                                    let barColor = 'bg-gray-200'

                                    if (percentage >= 90) {
                                      barColor = 'bg-green-300'
                                    } else if (percentage >= 60) {
                                      barColor = 'bg-orange-400'
                                    } else if (percentage > 0) {
                                      barColor = 'bg-red-400'
                                    }

                                    const discreteValues = [0, 25, 50, 75, 100]
                                    const displayPercentage =
                                      discreteValues.reduce((prev, curr) =>
                                        Math.abs(curr - percentage) <
                                        Math.abs(prev - percentage)
                                          ? curr
                                          : prev
                                      )

                                    return (
                                      <div
                                        className={`h-full rounded-full transition-all ${barColor}`}
                                        style={{
                                          width: `${displayPercentage}%`,
                                        }}
                                      />
                                    )
                                  })()}
                                </div>
                              </div>
                              <div className="space-y-2">
                                {(briefQuality?.criteria ?? []).map(
                                  (criterion, index) => (
                                    <div
                                      key={index}
                                      className="flex items-center gap-2"
                                    >
                                      <div
                                        className={cx(
                                          'w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center border-2 relative overflow-hidden',
                                          {
                                            'bg-gold-100 border-green-500':
                                              criterion.met &&
                                              criterion.score === 100,
                                            'bg-orange-200 border-orange-400':
                                              criterion.score < 100 &&
                                              criterion.score >= 51,
                                            'border-red-500 bg-red-500':
                                              criterion.score < 51,
                                          }
                                        )}
                                      >
                                        {criterion.met &&
                                          criterion.score === 100 && (
                                            <svg
                                              className="w-2.5 h-2.5 text-green-500 relative z-10"
                                              fill="none"
                                              stroke="currentColor"
                                              viewBox="0 0 24 24"
                                            >
                                              <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={3}
                                                d="M5 13l4 4L19 7"
                                              />
                                            </svg>
                                          )}
                                        {criterion.score < 100 &&
                                          criterion.score >= 51 && (
                                            <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-orange-500" />
                                          )}
                                      </div>
                                      <span className="text-sm text-base-700">
                                        {criterion.label}
                                      </span>
                                    </div>
                                  )
                                )}
                              </div>
                            </>
                          )}
                        </Accordion.Item>
                      </Accordion>
                    </div>
                  )}
              </>
            )}
          </>
        )}
        {briefStepsStatus === 'active' &&
          flags.showBriefingV2 &&
          pageState === 'generateBrief' &&
          (isNewFlow ? true : isUniversalBriefSelected) && (
            <ApplyBriefToAllTemplatesCheckbox />
          )}
        {brandVoiceId && !isOptimized && (
          <hr className="w-full my-6 border-gold-400" />
        )}
        {brandVoiceId && !isOptimized && (
          <BrandVoiceSettings
            brandVoiceId={brandVoiceId}
            isDisabled={isDisabled}
          />
        )}
      </div>
      {!isNewFlow && pageState === 'generateBrief' && (
        <div className="flex pt-6 justify-end items-center">
          <Button
            type="submit"
            data-cy="content-generate-brief-button"
            data-testid="content-generate-brief-button"
            variant="primary"
            disabled={formState.invalid || isLoading}
            suffixIcon={<ArrowRightIcon isDefaultColor={false} size={4} />}
            loading={isLoading}
            onClick={() => {
              form.change('submitAction', 'generateBrief')
            }}
          >
            Generate brief
          </Button>
        </div>
      )}
      {!isNewFlow &&
        (pageState === 'formChangedWithBrief' ||
          pageState === 'formChangedWithTopics') && (
          <div className="flex pt-6 justify-end items-center">
            <Button
              variant="secondary"
              className="mr-4"
              disabled={isLoading}
              onClick={() => form?.reset()}
            >
              Revert
            </Button>
            <Button
              type="submit"
              data-cy="content-refresh-brief-button"
              data-testid="content-refresh-brief-button"
              variant="primary"
              suffixIcon={<ArrowRightIcon isDefaultColor={false} size={4} />}
              loading={isLoading}
              disabled={formState.invalid || isLoading}
              onClick={() => {
                form.change('submitAction', 'refreshBrief')
              }}
            >
              Refresh brief
            </Button>
          </div>
        )}
      {pageState === 'generateWithoutTopics' && (
        <div className="flex pt-6 justify-end items-center">
          <Button
            type="submit"
            data-cy="content-generate-brief-button"
            data-testid="content-generate-brief-button"
            variant="primary"
            disabled={formState.invalid || isLoading}
            suffixIcon={<ArrowRightIcon isDefaultColor={false} size={4} />}
            loading={isLoading}
            onClick={() => {
              form.change('submitAction', 'generateWithoutTopics')
            }}
          >
            Generate
          </Button>
        </div>
      )}
      {pageState === 'formChangedWithoutTopics' && (
        <div className="flex pt-6 justify-end items-center">
          <Button
            variant="secondary"
            className="mr-4"
            disabled={isLoading}
            onClick={() => form?.reset()}
          >
            Revert
          </Button>
          <Button
            type="submit"
            data-cy="content-regenerate-variants-button"
            data-testid="content-regenerate-variants-button"
            variant="primary"
            loading={isLoading}
            disabled={formState.invalid || isLoading}
            suffixIcon={<ArrowRightIcon isDefaultColor={false} size={4} />}
            onClick={() => {
              form.change('submitAction', 'generateWithoutTopics')
            }}
          >
            Regenerate
          </Button>
        </div>
      )}
      {pageState === 'brandVoiceChanged' && (
        <div className="flex pt-6 justify-end items-center">
          <Button
            variant="secondary"
            className="mr-4"
            disabled={isLoading}
            onClick={() => form?.reset()}
          >
            Revert
          </Button>
          <Button
            type="submit"
            data-cy="content-regenerate-button"
            data-testid="content-regenerate-button"
            variant="primary"
            loading={isLoading}
            disabled={formState.invalid || isLoading}
            onClick={() => {
              form.change('submitAction', 'brandVoiceChanged')
            }}
          >
            Regenerate
          </Button>
        </div>
      )}
    </>
  )
}

export default DynamicForm
